// CIRCLE shape mode — drag a filled disc out from the press-time cell.
//
// The press cell is the disc CENTER; the drag distance in the drawing plane sets
// the radius, so the circle grows the further you pull out. It's a flat disc on
// the picked face's plane (DRAW grows on the surface you started from; ERASE
// carves a disc out of that face). The plane resolution, pick→target mapping and
// grid reconciliation are all shared with BOX via shape-fill; only the cell set
// (a disc instead of a rectangle) is specific here.

import { controllerStates } from "../../input/controllers";
import { type PaletteIndex, type VoxelGrid } from "../../model/voxel-grid";
import { recordSnapshot, suppressRecording } from "../../model/history";
import { type CellBounds, type PickTarget } from "../pick";
import { interaction, setInteraction } from "../state";
import type { Interaction } from "../types";
import {
  type Cell,
  getAxis,
  isDrawKind,
  planeFromPick,
  reconcileFill,
  setAxis,
  targetCellFromPick,
} from "./shape-fill";

type CircleState = Extract<
  Interaction,
  { kind: "circle_draw" } | { kind: "circle_erase" } | { kind: "circle_recolor" }
>;

// Snapshot-undo wiring (same as box/extrude): when wired, the disc reconcile
// runs with per-cell history SUPPRESSED and one snapshot diff is recorded at
// commit — removing the per-cell recordChange that made a big disc lag.
let captureSnapshot: (() => unknown) | null = null;
export function setCircleSnapshotCapture(fn: (() => unknown) | null): void {
  captureSnapshot = fn;
}
let circleSnapBefore: unknown = null;

// Commit the disc on release: record the one snapshot undo step. No-op on the
// per-cell fallback path (no capture wired).
export function commitCircle(): void {
  if (
    interaction.kind !== "circle_draw" &&
    interaction.kind !== "circle_erase" &&
    interaction.kind !== "circle_recolor"
  ) {
    return;
  }
  if (captureSnapshot && circleSnapBefore != null) {
    recordSnapshot(circleSnapBefore, captureSnapshot());
  }
  circleSnapBefore = null;
}


export function startCircle(
  pick: PickTarget,
  controllerIndex: number,
  action: "draw" | "erase" | "recolor",
  grid: VoxelGrid,
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
): void {
  const plane = planeFromPick(pick, action);
  if (!plane) return;

  const kind =
    action === "draw"
      ? "circle_draw"
      : action === "erase"
        ? "circle_erase"
        : "circle_recolor";
  const state: CircleState = {
    kind,
    controllerIndex,
    planeAxis: plane.planeAxis,
    planeCoord: plane.planeCoord,
    planeNormalSign: plane.planeNormalSign,
    startCell: { ...plane.startCell },
    currentCell: { ...plane.startCell },
    applied: new Map(),
    placedKeys: new Set(),
  };
  setInteraction(state);

  // Snapshot the pre-gesture grid for undo (before the first cell is written).
  circleSnapBefore = captureSnapshot ? captureSnapshot() : null;

  // Press commits the single center cell; drag grows the radius from there.
  syncDisc(state, grid, bounds, paletteIndex);
}

export function handleCircleDrag(
  lastPicks: ReadonlyArray<PickTarget | null>,
  grid: VoxelGrid,
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
): void {
  if (
    interaction.kind !== "circle_draw" &&
    interaction.kind !== "circle_erase" &&
    interaction.kind !== "circle_recolor"
  ) {
    return;
  }
  const state = interaction;
  if (!controllerStates[state.controllerIndex].selecting) return;
  const rim = targetCellFromPick(lastPicks[state.controllerIndex], isDrawKind(state.kind));
  if (!rim) return;
  setCircleRim(rim.ix, rim.iy, rim.iz, grid, bounds, paletteIndex);
}

// Set the disc's rim cell directly (radius = in-plane distance to the center) and
// re-reconcile. Shared by the VR pick path and the desktop plane-projection driver.
export function setCircleRim(
  ix: number,
  iy: number,
  iz: number,
  grid: VoxelGrid,
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
): void {
  if (
    interaction.kind !== "circle_draw" &&
    interaction.kind !== "circle_erase" &&
    interaction.kind !== "circle_recolor"
  ) {
    return;
  }
  const state = interaction;
  if (
    ix === state.currentCell.ix &&
    iy === state.currentCell.iy &&
    iz === state.currentCell.iz
  ) {
    return; // no change
  }
  state.currentCell = { ix, iy, iz };
  syncDisc(state, grid, bounds, paletteIndex);
}

function syncDisc(
  state: CircleState,
  grid: VoxelGrid,
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
): void {
  const run = (): void =>
    reconcileFill(state, grid, bounds, paletteIndex, () => iterateDiscCells(state));
  // Suppress per-cell history when snapshotting is wired (undo is the one
  // snapshot recorded at commit); the reconcile's retract still works.
  if (circleSnapBefore != null) suppressRecording(run);
  else run();
}

// Yield the cells of the filled disc: fixed at planeCoord along planeAxis,
// spanning the other two axes (u, v) within `radius` of the center. The radius is
// the rounded in-plane distance from center to rim, and a cell is included when
// it falls within (radius + 0.5)² — a discrete filled circle.
function* iterateDiscCells(state: CircleState): IterableIterator<Cell> {
  const { planeAxis, planeCoord } = state;
  const u: 0 | 1 | 2 = planeAxis === 0 ? 1 : 0; // the two in-plane axes
  const v: 0 | 1 | 2 = planeAxis === 2 ? 1 : 2;
  const cu = getAxis(state.startCell, u);
  const cv = getAxis(state.startCell, v);
  const du = getAxis(state.currentCell, u) - cu;
  const dv = getAxis(state.currentCell, v) - cv;
  const radius = Math.round(Math.hypot(du, dv));
  const thr = (radius + 0.5) * (radius + 0.5);
  for (let su = -radius; su <= radius; su++) {
    for (let sv = -radius; sv <= radius; sv++) {
      if (su * su + sv * sv > thr) continue;
      const cell: Cell = { ix: 0, iy: 0, iz: 0 };
      setAxis(cell, planeAxis, planeCoord);
      setAxis(cell, u, cu + su);
      setAxis(cell, v, cv + sv);
      yield cell;
    }
  }
}
